import { useApplication } from '@/hooks/useApplication';
import { useDialogs } from '@/hooks/useDialogs';
import { useMetadata } from '@/hooks/useMetadata';
import { useSubroutine } from '@/hooks/useSubroutine';
import { useUpdateNodes } from '@/hooks/useUpdateNodes';
import { Application } from '@/types/application';
import { Block } from '@/types/metadata/metadata';
import { addBlock, findSubroutine, setOrReplaceAppStartblock, setOrReplaceStartblock } from '@/util/application';
import { getColorFromString } from '@/util/color';
import { Clear } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircleIcon from '@mui/icons-material/Circle';
import SearchIcon from '@mui/icons-material/Search';
import {
  Card,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  useTheme
} from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { XYPosition, useReactFlow } from '@xyflow/react';
import { FC, MouseEvent, useState } from 'react';

type NewBlockDialogProps = {
  open: boolean;
  onClose: () => void;
  position?: XYPosition;
};

const NewBlockDialog: FC<NewBlockDialogProps> = (props) => {
  const { metadata } = useMetadata();
  const { application, setApplication } = useApplication();
  const { selectedSubroutineId } = useSubroutine();
  const { setUpdateNodes } = useUpdateNodes();
  const { confirm } = useDialogs();
  const theme = useTheme();
  const colorShade = theme.palette.mode === 'dark' ? 900 : 200;

  const [metaBlock, setMetaBlock] = useState<Block>();
  const [blockLabel, setBlockLabel] = useState('');
  const [blockQuery, setBlockQuery] = useState('');
  const { screenToFlowPosition } = useReactFlow();

  const subroutine =
    application && selectedSubroutineId && findSubroutine(application.subroutines, selectedSubroutineId);

  const handleClose = () => {
    setBlockLabel('');
    setBlockQuery('');
    setMetaBlock(undefined);
    props.onClose();
  };

  const addBlockToApplication = (
    blockLabel: string,
    metaBlock: Block,
    event: MouseEvent<HTMLButtonElement | HTMLDivElement, globalThis.MouseEvent>,
    baseApplication: Application = application
  ) => {
    if (!selectedSubroutineId) return;
    const addedBlock = addBlock(
      metadata,
      baseApplication,
      selectedSubroutineId,
      metaBlock,
      props.position ||
        screenToFlowPosition({
          x: event.pageX / 2,
          y: event.pageY / 2
        }),
      blockLabel
    );
    setApplication(addedBlock.application);
    setUpdateNodes([addedBlock.newId.toString()]);
    handleClose();
  };

  const handleAdd = (
    event: MouseEvent<HTMLButtonElement | HTMLDivElement, globalThis.MouseEvent>,
    providedMetaBlock?: Block
  ) => {
    if (!selectedSubroutineId || (!metaBlock && !providedMetaBlock)) return;
    const block = providedMetaBlock || metaBlock;
    if (!block) throw new Error('No block provided');
    const currentStartblock = findSubroutine(application.subroutines, selectedSubroutineId)?.startblock;
    const nodeIds = Object.keys(application.blocks.nodes)
      .map((id) => Number.parseInt(id))
      .filter((id) => id !== currentStartblock);
    const newId = Math.max(...nodeIds, 0) + 1;

    switch (block.style) {
      case 'appstart':
        setOrReplaceAppStartblock(newId, application, metadata, selectedSubroutineId, confirm).then(
          (newApplication) => {
            addBlockToApplication(blockLabel, block, event, newApplication);
          }
        );
        break;

      case 'subroutine':
        setOrReplaceStartblock(newId, application, selectedSubroutineId, confirm).then((newApplication) => {
          addBlockToApplication(blockLabel, block, event, newApplication);
        });
        break;

      default:
        addBlockToApplication(blockLabel, block, event);
        break;
    }
  };

  const listedBlocks = metadata.blocks
    .filter((block) => {
      // Return true (include item in the filtered data) if query is empty or it matches the item's name
      return (
        (blockQuery === '' ||
          block.defaultlabel?.toLowerCase().includes(blockQuery.toLowerCase()) ||
          block.description?.toLowerCase().includes(blockQuery.toLowerCase())) &&
        block.style !== 'note'
      );
    })
    .toSorted((a, b) => a.defaultlabel.localeCompare(b.defaultlabel));

  return (
    <Dialog open={props.open} onClose={handleClose} fullWidth disableRestoreFocus>
      <DialogTitle>
        New Block
        {subroutine && (
          <>
            {' '}
            in subroutine <code>{subroutine.label}</code>
          </>
        )}
      </DialogTitle>
      <DialogContent
        sx={{
          minHeight: theme.spacing(40),
          display: 'flex',
          flexDirection: 'column',
          gap: 1
        }}
      >
        <Card
          variant='outlined'
          sx={{
            bgcolor: 'transparent',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <TextField
            label='Search'
            autoFocus={props.open}
            value={blockQuery}
            onChange={(event) => setBlockQuery(event.target.value)}
            InputProps={{
              autoComplete: 'off',
              endAdornment: (
                <InputAdornment position='end'>
                  {blockQuery ? (
                    <IconButton size='small' onClick={() => setBlockQuery('')}>
                      <Clear />
                    </IconButton>
                  ) : (
                    <SearchIcon />
                  )}
                </InputAdornment>
              )
            }}
            sx={{ minWidth: theme.spacing(40), mt: 1 }}
            size='small'
          />
          <List
            sx={{
              maxHeight: theme.spacing(50),
              overflowY: 'auto',
              width: '100%'
            }}
          >
            {listedBlocks.map((block) => {
              const isSelected = block.defaultlabel === metaBlock?.defaultlabel;
              return (
                <ListItemButton
                  key={block.type}
                  selected={isSelected}
                  disabled={Boolean(block.obsolete)}
                  onClick={() => setMetaBlock(block)}
                  onDoubleClick={(e) => handleAdd(e, block)}
                >
                  <ListItemIcon>
                    {isSelected ? (
                      <CheckCircleIcon
                        sx={{
                          color: getColorFromString(block.style, colorShade)
                        }}
                      />
                    ) : (
                      <CircleIcon
                        sx={{
                          color: block.obsolete ? 'red' : getColorFromString(block.style, colorShade)
                        }}
                      />
                    )}
                  </ListItemIcon>
                  <ListItemText primary={block.defaultlabel} secondary={block.description} />
                </ListItemButton>
              );
            })}
          </List>
        </Card>
        <DialogContentText variant='subtitle2'>
          When you press Add, the block will be added to the center of the current viewport.
        </DialogContentText>
        <TextField
          label='Label'
          value={blockLabel}
          fullWidth
          onChange={(e) => setBlockLabel(e.target.value)}
          size='small'
          InputProps={{ autoComplete: 'off' }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color='secondary'>
          Cancel
        </Button>
        <Button onClick={handleAdd} color='primary' variant='contained' disabled={!metaBlock}>
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewBlockDialog;
